import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { SERVICES, IMAGES } from "@/data/content";
import { ArrowUpRight } from "lucide-react";

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] },
  }),
};

const BG_FOR = {
  cheat: IMAGES.gaming,
  malware: IMAGES.padlock,
};

function ServiceCard({ s, i }) {
  const bg = BG_FOR[s.id];
  return (
    <motion.article
      custom={i}
      variants={cardVariants}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
      data-testid={`service-card-${s.id}`}
      className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-[#0A0A12] ${s.span}`}
    >
      <Link to={`/${s.slug}`} className="block p-8 lg:p-10 min-h-[240px] flex flex-col justify-between" data-testid={`service-link-${s.slug}`}>
      {bg && (
        <>
          <img src={bg} alt={s.title} className="absolute inset-0 w-full h-full object-cover opacity-15 group-hover:opacity-25 transition-opacity duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A12] via-[#0A0A12]/70 to-transparent" />
        </>
      )}
      {/* tracing beam glow */}
      <div
        className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ boxShadow: `inset 0 0 0 1px ${s.accent}55, 0 0 40px ${s.accent}22` }}
      />
      <div className="relative flex items-start justify-between">
        <span className="font-mono text-xs tracking-[0.25em]" style={{ color: s.accent }}>
          / {s.tag}
        </span>
        <ArrowUpRight className="w-5 h-5 text-slate-600 group-hover:text-white group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-300" />
      </div>
      <div className="relative mt-8">
        <h3 className="font-display font-700 text-2xl lg:text-3xl tracking-tight text-white leading-tight">
          {s.title}
        </h3>
        <p className="mt-4 text-sm text-slate-400 leading-relaxed max-w-md">{s.desc}</p>
        <span className="mt-5 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-[#22D3EE] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          Learn more
        </span>
      </div>
      </Link>
    </motion.article>
  );
}

export default function Services() {
  return (
    <section id="services" className="relative py-28 lg:py-40" data-testid="services-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <span className="text-xs uppercase tracking-[0.3em] text-[#22D3EE]">What we protect</span>
            <h2 className="mt-5 font-display font-900 text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-white leading-none max-w-2xl">
              One perimeter.<br />Every threat.
            </h2>
          </div>
          <p className="max-w-sm text-sm text-slate-400 leading-relaxed">
            Six layers of defense and delivery, orchestrated from a single control plane
            that spans the globe.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 lg:gap-5">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.id} s={s} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
